import React, { useState, useMemo } from "react";
import LivePriceData from "./PriceHeader/LivePrice";
import {
  ExternalLink,
  Star,
  Award,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { exchanges } from "../data/exchanges";
import { useDenomination } from "../contexts/DenominationContext";
import DenominationToggle from "./DenominationToggle";
import { usePriceUpdates } from "../hooks/usePriceUpdates";

export default function ExchangeTable() {
  const [amount, setAmount] = useState("1,000");
  const [isBuying, setIsBuying] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [activeType, setActiveType] = useState<string>("All");
  const [deductWithdrawalFees, setDeductWithdrawalFees] =
    useState<boolean>(true);

  const { formatBitcoin } = useDenomination();
  const { price: livePrice, exchangePrices } = usePriceUpdates();

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, "");
    const formattedValue = Number(numericValue).toLocaleString();
    setAmount(formattedValue);
  };

  const numericAmount = parseFloat(amount.replace(/,/g, "")) || 0;

  // Helper: pick lowest deposit fee (CAD) from depositMethods for given CAD amount
  const getDepositFeeCAD = (exchange: any, cadAmount: number): number => {
    if (!exchange.depositMethods) return 0;
    let minFee = Infinity;
    exchange.depositMethods.forEach((method: any) => {
      if (!method.tiers) return;
      method.tiers.forEach((tier: any) => {
        let fee = Infinity;
        // Determine if tier applies based on minAmount/maxAmount (in CAD)
        const minAmt = tier.minAmount ?? 0;
        const maxAmt = tier.maxAmount ?? Infinity;
        if (cadAmount >= minAmt && cadAmount <= maxAmt) {
          if (tier.feeType === "percentage") {
            fee = cadAmount * (tier.feeValue / 100);
          } else if (tier.feeType === "flat") {
            fee = tier.feeValue;
          } else {
            // note type or unspecified: treat as zero or skip if note indicates covered
            fee = 0;
          }
          if (fee < minFee) {
            minFee = fee;
          }
        }
      });
    });
    return minFee === Infinity ? 0 : minFee;
  };

  // Helper: pick lowest withdrawal fee in BTC for buy side (withdraw BTC)
  const getWithdrawalFeeBTC = (exchange: any): number => {
    if (!exchange.withdrawalMethods) return 0;
    let minFee = Infinity;
    exchange.withdrawalMethods.forEach((method: any) => {
      if (!method.tiers) return;
      method.tiers.forEach((tier: any) => {
        if (tier.currency === "BTC") {
          // For BTC tiers: feeValue is in BTC if feeType flat; if percentage, can't apply here
          if (tier.feeType === "flat") {
            const feeBtc = tier.feeValue;
            if (feeBtc < minFee) minFee = feeBtc;
          } else {
            // percentage withdrawal in BTC: ambiguous; skip
          }
        }
      });
    });
    return minFee === Infinity ? 0 : minFee;
  };

  // Helper: pick lowest withdrawal fee in CAD for sell side (withdraw CAD)
  const getWithdrawalFeeCAD = (exchange: any, cadAmount: number): number => {
    if (!exchange.withdrawalMethods) return 0;
    let minFee = Infinity;
    exchange.withdrawalMethods.forEach((method: any) => {
      if (!method.tiers) return;
      method.tiers.forEach((tier: any) => {
        // Only consider tiers with CAD currency or percentage
        if (tier.currency === "CAD" || tier.currency === "%") {
          const minAmt = tier.minAmount ?? 0;
          const maxAmt = tier.maxAmount ?? Infinity;
          if (cadAmount >= minAmt && cadAmount <= maxAmt) {
            let fee = 0;
            if (tier.feeType === "flat") {
              fee = tier.feeValue;
            } else if (tier.feeType === "percentage") {
              fee = cadAmount * (tier.feeValue / 100);
            } else {
              // note or unspecified: treat as zero
              fee = 0;
            }
            if (fee < minFee) minFee = fee;
          }
        }
      });
    });
    return minFee === Infinity ? 0 : minFee;
  };

  // Helper: get trading fee percentage for buy or sell based on tradingFees structure
  // Now falls back to exchange.spreadFee if tradingFees tiers lack explicit percentages.
  const getTradingFeePercent = (
    exchange: any,
    cadAmount: number,
    isBuy: boolean
  ): number => {
    const tf = exchange.tradingFees;
    if (!tf || !tf.type || !Array.isArray(tf.tiers)) {
      // fallback to spreadFee if defined
      return exchange.spreadFee ?? 0;
    }
    const amount = cadAmount;

    if (tf.type === "spread") {
      const tier = tf.tiers[0];
      if (tier.feeValueMin != null) {
        return tier.feeValueMin;
      }
      // fallback
      return exchange.spreadFee ?? 0;
    }

    if (tf.type === "range") {
      const tier = tf.tiers[0];
      if (tier.feeValueMax != null) return tier.feeValueMax;
      if (tier.feeValueMin != null) return tier.feeValueMin;
      return exchange.spreadFee ?? 0;
    }

    if (tf.type === "tiered") {
      // 1) volume-based tiers with maker/taker
      const volumeTiers = tf.tiers.filter(
        (t: any) =>
          (t.makerFeePercent != null || t.takerFeePercent != null) &&
          (t.volumeMin != null || t.volumeMax != null)
      );
      if (volumeTiers.length > 0) {
        for (const tier of volumeTiers) {
          const minV = tier.volumeMin ?? 0;
          const maxV = tier.volumeMax ?? Infinity;
          if (amount >= minV && amount <= maxV) {
            if (isBuy && tier.takerFeePercent != null)
              return tier.takerFeePercent;
            if (!isBuy && tier.takerFeePercent != null)
              return tier.takerFeePercent;
            if (tier.makerFeePercent != null) return tier.makerFeePercent;
          }
        }
        const last = volumeTiers[volumeTiers.length - 1];
        if (isBuy && last.takerFeePercent != null) return last.takerFeePercent;
        if (!isBuy && last.takerFeePercent != null) return last.takerFeePercent;
        if (last.makerFeePercent != null) return last.makerFeePercent;
        return exchange.spreadFee ?? 0;
      }
      // 2) client-level or asset-level tiers with feeValueMin/feeValueMax
      const percentTiers = tf.tiers.filter((t: any) => t.feeValueMin != null);
      if (percentTiers.length > 0) {
        let worst = 0;
        percentTiers.forEach((tier: any) => {
          if (tier.feeValueMin! > worst) worst = tier.feeValueMin!;
        });
        return worst;
      }
      // 3) tiers without explicit percentages: fallback to spreadFee
      return exchange.spreadFee ?? 0;
    }

    if (tf.type === "maker-taker") {
      const tier = tf.tiers[0];
      if (isBuy && tier.takerFeePercent != null) return tier.takerFeePercent;
      if (!isBuy && tier.takerFeePercent != null) return tier.takerFeePercent;
      if (tier.makerFeePercent != null) return tier.makerFeePercent;
      return exchange.spreadFee ?? 0;
    }

    // fallback
    return exchange.spreadFee ?? 0;
  };

  const processedExchanges = useMemo(() => {
    return exchanges.map((exchange: any) => {
      // BUY side: user has CAD amount numericAmount, wants BTC
      let buyCadAmount = numericAmount;
      // Deposit fee in CAD
      const depositFeeCAD = getDepositFeeCAD(exchange, buyCadAmount);
      const afterDepositCAD = Math.max(0, buyCadAmount - depositFeeCAD);

      // Trading fee percent
      const tradingFeePercentBuy = getTradingFeePercent(
        exchange,
        buyCadAmount,
        true
      );
      const afterTradingCAD =
        afterDepositCAD * (1 - tradingFeePercentBuy / 100);

      // Convert to BTC
      let buyBtcAmount = afterTradingCAD / livePrice;

      // Withdrawal fee in BTC (conditionally deducted)
      const withdrawalFeeBTC = deductWithdrawalFees
        ? getWithdrawalFeeBTC(exchange)
        : 0;
      buyBtcAmount = Math.max(0, buyBtcAmount - withdrawalFeeBTC);

      // SELL side: user has BTC amount = numericAmount / livePrice
      const btcToSell = numericAmount / livePrice;
      // Convert to CAD gross
      const grossCad = btcToSell * livePrice;
      // Trading fee percent for sell
      const tradingFeePercentSell = getTradingFeePercent(
        exchange,
        grossCad,
        false
      );
      let sellCadAfterTrading = grossCad * (1 - tradingFeePercentSell / 100);

      // Withdrawal fee in CAD (conditionally deducted)
      const withdrawalFeeCadSell = deductWithdrawalFees
        ? getWithdrawalFeeCAD(exchange, sellCadAfterTrading)
        : 0;
      const sellCadFinalAmount = Math.max(
        0,
        sellCadAfterTrading - withdrawalFeeCadSell
      );

      return {
        exchange,
        buyAmount: buyBtcAmount,
        sellAmount: sellCadFinalAmount,
        buyTotal: buyBtcAmount * livePrice,
        sellTotal: sellCadFinalAmount,
      };
    });
  }, [numericAmount, livePrice, deductWithdrawalFees]);

  const sortedExchanges = useMemo(() => {
    const sorted = [...processedExchanges];
    if (isBuying) {
      sorted.sort((a, b) => b.buyAmount - a.buyAmount);
    } else {
      sorted.sort((a, b) => b.sellAmount - a.sellAmount);
    }

    const bestValue = sorted[0][isBuying ? "buyAmount" : "sellAmount"];
    return sorted.map((item, index) => {
      const currentValue = item[isBuying ? "buyAmount" : "sellAmount"];
      const difference =
        index === 0
          ? 0
          : isBuying
          ? Math.abs(numericAmount / livePrice - currentValue) * livePrice
          : Math.abs(bestValue - currentValue);
      return {
        ...item,
        difference,
      };
    });
  }, [processedExchanges, isBuying, livePrice]);

  const filteredExchanges: any = sortedExchanges.filter(
    (item) => activeType === "All" || item.exchange.type === activeType
  );

  const displayedExchanges = showAll
    ? filteredExchanges
    : filteredExchanges.slice(0, 8);

  const formatDifference = (value: number) => {
    return new Intl.NumberFormat("en-CA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Mobile layout - single row for controls */}
      <div className="flex flex-row items-stretch sm:items-stretch gap-2 sm:gap-4">
        <button
          onClick={() => setIsBuying(!isBuying)}
          className={`min-w-20 flex-none flex items-center justify-center gap-1 px-3 h-14 sm:h-12 text-base font-medium border-2 ${
            isBuying
              ? "border-green-500 text-green-600 bg-green-50"
              : "border-red-500 text-red-600 bg-red-50"
          }`}
        >
          <span className="text-lg">{isBuying ? "↑" : "↓"}</span>
          <span>{isBuying ? "BUY" : "SELL"}</span>
        </button>

        <div className="flex-1 relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">
            $
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={amount}
            onChange={handleAmountChange}
            className="w-full h-14 sm:h-12 pl-8 pr-4 bg-[--bg-secondary] border-2 border-[--border-color] focus:border-[--accent] text-lg"
            placeholder="$1,000"
          />
        </div>

        <DenominationToggle />
      </div>

      <div className="hidden sm:block">
        <LivePriceData />
      </div>

      {/* filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex flex-row gap-2 sm:gap-4 items-center justify-center sm:justify-normal">
          <button
            type="button"
            onClick={() => setActiveType("All")}
            className={`border border-[--border-color] py-1 px-2 text-center ${
              activeType === "All" && "bg-black text-white"
            }`}
          >
            <span className="hidden sm:inline-block">Show</span> All
          </button>
          <button
            type="button"
            onClick={() => setActiveType("Custodial")}
            className={`border border-[--border-color] py-1 px-2 text-center ${
              activeType === "Custodial" && "bg-black text-white"
            }`}
          >
            <span className="hidden sm:inline-block">Show Only</span> Custodial
          </button>
          <button
            type="button"
            onClick={() => setActiveType("Non Custodial")}
            className={`border border-[--border-color] py-1 px-2 text-center ${
              activeType === "Non Custodial" && "bg-black text-white"
            }`}
          >
            <span className="hidden sm:inline-block">Show Only</span>{" "}
            Non-Custodial
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDeductWithdrawalFees(true)}
            className={`border border-[--border-color] py-1 px-2 text-center flex flex-row items-center ${
              deductWithdrawalFees && "bg-black text-white"
            }`}
          >
            Include Withdrawal <span className="hidden md:block">Fees</span>
          </button>
          <button
            type="button"
            onClick={() => setDeductWithdrawalFees(false)}
            className={`border border-[--border-color] py-1 px-2 text-center flex flex-row items-center ${
              !deductWithdrawalFees && "bg-black text-white"
            }`}
          >
            Exclude Withdrawal <span className="hidden md:block">Fees</span>
          </button>
        </div>
      </div>

      {/* Desktop Table View (hidden on mobile) */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full border-collapse min-w-[800px] border-2 border-[--border-color]">
          <thead>
            <tr className="border-b-2 border-[--border-color] bg-[--bg-secondary]">
              <th className="px-4">
                <div className="w-full max-w-[90px]"></div>
              </th>
              <th className="py-4 px-4 text-left font-bold">EXCHANGE</th>
              <th className="py-4 px-4 text-left font-bold">BTC/CAD</th>
              <th className="py-4 px-4 text-left font-bold">METHOD</th>
              <th className="py-4 px-4 text-left font-bold">FEE STRUCTURE</th>
              <th className="py-4 px-4 text-right font-bold">
                YOU {isBuying ? "GET" : "RECEIVE"}
              </th>
              <th className="py-4 px-4 text-right font-bold">DIFF</th>
            </tr>
          </thead>
          <tbody>
            {displayedExchanges.map((item: any, index: number) => (
              <tr
                key={item.exchange.id}
                className={`border-b-2 border-[--border-color] hover:bg-[--bg-secondary]
                  ${index === 0 ? "bg-[--bg-secondary]" : ""}`}
              >
                <td className="p-4">
                  <a href={item.exchange.url}>
                    <img
                      src={item.exchange.image}
                      alt={`${item.exchange.name}`}
                      className="max-h-[70px] w-full max-w-[90px] min-w-[50px] object-contain rounded-lg"
                    />
                  </a>
                </td>

                <td className="">
                  <a
                    href={item.exchange.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[--accent] hover:underline font-bold"
                  >
                    {item.exchange.name}
                    <ExternalLink className="h-4 w-4" />
                    <p
                      className={` text-white py-[2px] text-xs px-2 text-center mr-auto max-w-[120px] ${
                        item.exchange.type === "Custodial"
                          ? "bg-[--accent]"
                          : "bg-black"
                      }`}
                      style={{ borderRadius: "12px" }}
                    >
                      {item.exchange.type}
                    </p>
                  </a>
                  <div className="text-sm text-[--text-secondary] mt-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span>{item.exchange.details}</span>

                      {item.exchange.referralPromo && (
                        <span className="inline-flex items-center gap-1 bg-green-50 text-green-600 px-2 py-0.5 text-xs font-medium">
                          <Award className="h-3 w-3" />
                          {item.exchange.referralPromo}
                        </span>
                      )}
                    </div>

                    {/* Features */}
                    {item.exchange.depositMethods &&
                      item.exchange.depositMethods.length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-2 text-xs">
                          <span className="text-gray-500">Methods:</span>
                          {item.exchange.depositMethods
                            .slice(0, 2)
                            .map((method: any, i: number) => (
                              <span key={i} className="text-gray-600">
                                {method.type}
                                {/* We don't show fee here as-tier details are complex; could add tooltip if desired */}
                                {i <
                                  Math.min(
                                    item.exchange.depositMethods.length - 1,
                                    1
                                  ) && ","}
                              </span>
                            ))}
                          {item.exchange.depositMethods.length > 2 && (
                            <span className="text-gray-600">
                              +{item.exchange.depositMethods.length - 2} more
                            </span>
                          )}
                        </div>
                      )}

                    {/* Reviews */}
                    {item.exchange.ratings &&
                      (item.exchange.ratings.apple ||
                        item.exchange.ratings.google) && (
                        <div className="flex items-center gap-2 text-xs">
                          <Star className="h-3 w-3 text-yellow-500" />
                          <div className="flex gap-2">
                            {item.exchange.ratings.apple && (
                              <span className="text-gray-600">
                                <span className="font-medium">App Store:</span>{" "}
                                {item.exchange.ratings.apple}
                              </span>
                            )}
                            {item.exchange.ratings.apple &&
                              item.exchange.ratings.google && (
                                <span className="text-gray-400">|</span>
                              )}
                            {item.exchange.ratings.google && (
                              <span className="text-gray-600">
                                <span className="font-medium">Play Store:</span>{" "}
                                {item.exchange.ratings.google}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </td>
                <td className="py-4 px-4 text-left">
                  {exchangePrices?.[item.exchange.id] != null
                    ? `$${exchangePrices[item.exchange.id].toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }
                      )}`
                    : `$${livePrice.toLocaleString()} CAD`}
                </td>
                <td className="py-4 px-4 font-bold">
                  {item.exchange.depositType}
                </td>
                <td className="py-4 px-4 text-left leading-[18px]">
                  {item.exchange.tradeFee}
                </td>
                <td className="py-4 px-4 text-right">
                  <div className="font-bold">
                    {isBuying
                      ? formatBitcoin(item.buyAmount)
                      : `$${item.sellAmount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`}
                  </div>
                  <div className="text-[--text-secondary]">
                    {isBuying
                      ? `$${(item.buyAmount * livePrice).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )}`
                      : formatBitcoin(numericAmount / livePrice)}
                  </div>
                </td>
                <td className="py-4 px-4 text-right font-bold">
                  {index === 0 ? (
                    <span className="text-green-500">BEST</span>
                  ) : (
                    <span className="text-[--text-secondary] whitespace-nowrap">
                      -${formatDifference(item.difference)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden space-y-3">
        {displayedExchanges.map((item: any, index: number) => (
          <div
            key={item.exchange.id}
            className={`border-2 border-[--border-color] overflow-hidden ${
              index === 0 ? "bg-[--bg-secondary]" : "bg-white"
            }`}
          >
            <div className="flex items-start justify-between p-3 border-b border-[--border-color]">
              <div>
                <a href={item.exchange.url}>
                  <img
                    src={item.exchange.image}
                    alt={`${item.exchange.name}`}
                    className="max-h-[60px] mb-2 w-full max-w-[80px] min-w-[50px] object-contain rounded-lg"
                  />
                </a>
                <a
                  href={item.exchange.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[--accent] font-bold text-lg"
                >
                  {item.exchange.name}
                  <ExternalLink className="h-4 w-4" />
                </a>
                <div className="text-sm text-[--text-secondary] mt-1">
                  {item.exchange.details}
                </div>
              </div>
              {index === 0 && (
                <span className="bg-green-100 text-green-600 px-2 py-1 text-xs font-medium">
                  BEST
                </span>
              )}
            </div>

            <div className="p-3 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-[--text-secondary]">METHOD</div>
                  <div className="font-bold">{item.exchange.depositType}</div>
                </div>
                <div>
                  <div className="text-xs text-[--text-secondary]">FEE</div>
                  <div className="font-bold">{item.exchange.tradeFee}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-[--text-secondary]">
                    YOU {isBuying ? "GET" : "RECEIVE"}
                  </div>
                  <div className="font-mono break-words">
                    <div className="font-medium">
                      {isBuying
                        ? formatBitcoin(item.buyAmount)
                        : `$${item.sellAmount.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`}
                    </div>
                    <div className="text-xs text-[--text-secondary]">
                      {isBuying
                        ? `$${(item.buyAmount * livePrice).toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}`
                        : formatBitcoin(numericAmount / livePrice)}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-[--text-secondary]">DIFF</div>
                  <div className="font-bold">
                    {index === 0 ? (
                      <span className="text-green-500">BEST</span>
                    ) : (
                      <span className="text-[--text-secondary] whitespace-nowrap">
                        -${formatDifference(item.difference)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-[--text-secondary]">BTC/CAD</p>
                  {exchangePrices?.[item.exchange.id] != null
                    ? `$${exchangePrices[item.exchange.id].toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }
                      )}`
                    : `$${livePrice.toLocaleString()} CAD`}
                </div>
              </div>

              {/* Additional info */}
              {item.exchange.referralPromo && (
                <div className="mt-2">
                  <span className="inline-flex items-center gap-1 bg-green-50 text-green-600 px-2 py-0.5 text-xs font-medium">
                    <Award className="h-3 w-3" />
                    {item.exchange.referralPromo}
                  </span>
                </div>
              )}

              {/* Methods */}
              {item.exchange.depositMethods &&
                item.exchange.depositMethods.length > 0 && (
                  <div className="flex flex-wrap items-center gap-x-2 text-xs mt-2">
                    <span className="text-gray-500">Methods:</span>
                    {item.exchange.depositMethods
                      .slice(0, 2)
                      .map((method: any, i: number) => (
                        <span key={i} className="text-gray-600">
                          {method.type}
                          {i <
                            Math.min(
                              item.exchange.depositMethods.length - 1,
                              1
                            ) && ","}
                        </span>
                      ))}
                  </div>
                )}

              {/* Ratings */}
              {item.exchange.ratings &&
                (item.exchange.ratings.apple ||
                  item.exchange.ratings.google) && (
                  <div className="flex items-center gap-2 text-xs mt-1">
                    <Star className="h-3 w-3 text-yellow-500" />
                    <div className="flex flex-wrap gap-2">
                      {item.exchange.ratings.apple && (
                        <span className="text-gray-600">
                          <span className="font-medium">App:</span>{" "}
                          {item.exchange.ratings.apple}
                        </span>
                      )}
                      {item.exchange.ratings.apple &&
                        item.exchange.ratings.google && (
                          <span className="text-gray-400 hidden sm:inline">
                            |
                          </span>
                        )}
                      {item.exchange.ratings.google && (
                        <span className="text-gray-600">
                          <span className="font-medium">Play:</span>{" "}
                          {item.exchange.ratings.google}
                        </span>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>

      <div className="w-full mt-2 sm:mt-4">
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[--bg-secondary] border-2 border-[--border-color] hover:bg-gray-100 text-[--text-primary] font-bold transition-colors"
        >
          {showAll ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Show Top 8 Exchanges
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Show All Exchanges ({filteredExchanges.length})
            </>
          )}
        </button>
      </div>
    </div>
  );
}
